import { useState } from "react";
import { Check, ChevronDown, Circle, CircleDot } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useCenaHistoria, useUpdateZakazka } from "@/api/hooks/useZakazky";
import { STAV_LABELS, StavBadge } from "@/components/zakazka/StavBadge";
import type { StavZakazky } from "@/api/schemas";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const CESTA: StavZakazky[] = ["ponuka", "v_priprave", "vo_vyrobe", "dokoncena"];

/** Kedy sa do kroku prejde — pri krokoch, ktoré zákazka ešte nemá. */
const KEDY: Record<StavZakazky, string> = {
  ponuka: "výpočet bez čísla AA",
  v_priprave: "číslo AA / Z alebo výrobná varianta",
  vo_vyrobe: "pri odoslaní na pílu",
  dokoncena: "ručne — keď je vyrobené",
};

/**
 * Stav zákazky ako tlačidlo s cestou (KOLO 239, návrh S1 bez Na sklade).
 *
 * Ponuka → V príprave → Vo výrobe idú samy (číslo AA, píla); Dokončenú
 * označí človek (user: *„musí sa ručne určiť, či sa to vyrobilo"*)
 * a Kalkulácia ju presunie do Archívu. Dátumy krokov sú zo snímok ceny,
 * ktoré server zapisuje pri každom prechode — načítajú sa až pri otvorení.
 */
export function StavZakazkyMenu({
  zakazka,
}: {
  zakazka: {
    id: string;
    cislo_zakazky: string;
    stav: StavZakazky;
    fakturovana?: boolean;
    deleted_at?: string | null;
  };
}): JSX.Element {
  const [otvorene, setOtvorene] = useState(false);
  const historia = useCenaHistoria(zakazka.id, otvorene);
  const update = useUpdateZakazka();
  const snimky = historia.data?.data.snapshoty ?? [];
  const index = CESTA.indexOf(zakazka.stav);
  const archivovana = !!zakazka.deleted_at;

  /** Najnovší prechod do stavu (snímky sú najnovšie prvé). */
  function kedy(stav: StavZakazky): string | null {
    const s = snimky.find((x) => x.stav_do === stav);
    return s ? `${formatDateTime(s.created_at)} · ${s.autor}` : null;
  }

  // Späť z Dokončenej tam, kde zákazka bola — na pílu išla, ak má snímku
  // „→ vo výrobe"; bez histórie (načítava sa) sa tlačidlo nepustí
  const spat: StavZakazky = snimky.some((s) => s.stav_do === "vo_vyrobe")
    ? "vo_vyrobe"
    : "v_priprave";

  async function nastav(stav: StavZakazky, hlaska: string): Promise<void> {
    try {
      await update.mutateAsync({ id: zakazka.id, data: { stav } });
      toast({ title: hlaska, description: zakazka.cislo_zakazky });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Stav sa nezmenil",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <DropdownMenu open={otvorene} onOpenChange={setOtvorene}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Stav zákazky: ${STAV_LABELS[zakazka.stav]}`}
          title="Cesta zákazky"
        >
          <StavBadge stav={zakazka.stav} />
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Cesta zákazky
        </DropdownMenuLabel>
        <ol className="space-y-1 px-2 pb-2" aria-label="Cesta zákazky">
          {CESTA.map((stav, i) => {
            const hotovy = i < index || (stav === "dokoncena" && i === index);
            const aktualny = i === index;
            const datum = i <= index ? kedy(stav) : null;
            return (
              <li key={stav} className="flex items-start gap-2 text-sm">
                {aktualny && stav !== "dokoncena" ? (
                  <CircleDot className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                ) : hotovy ? (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                ) : (
                  <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" />
                )}
                <span className="min-w-0">
                  <span
                    className={cn(
                      aktualny && "font-semibold",
                      i > index && "text-muted-foreground",
                    )}
                  >
                    {STAV_LABELS[stav]}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {datum ?? (i > index ? KEDY[stav] : "")}
                  </span>
                </span>
              </li>
            );
          })}
          <li className="flex items-start gap-2 text-sm">
            {zakazka.fakturovana ? (
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            ) : (
              <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50" />
            )}
            <span>
              <span
                className={cn(!zakazka.fakturovana && "text-muted-foreground")}
              >
                Kalkulácia
              </span>
              <span className="block text-xs text-muted-foreground">
                {!zakazka.fakturovana
                  ? "fajkou — zákazka pôjde do Archívu"
                  : archivovana
                    ? "zákazka je v Archíve"
                    : "hotová — zákazka obnovená z Archívu"}
              </span>
            </span>
          </li>
        </ol>
        {archivovana ? null : zakazka.stav === "v_priprave" ||
          zakazka.stav === "vo_vyrobe" ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => void nastav("dokoncena", "Zákazka je dokončená")}
            >
              <Check className="mr-2 h-4 w-4" />
              Označiť ako dokončenú (vyrobené)
            </DropdownMenuItem>
          </>
        ) : zakazka.stav === "dokoncena" ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={historia.isLoading}
              onSelect={() => {
                if (
                  window.confirm(
                    `Vrátiť zákazku ${zakazka.cislo_zakazky} z Dokončenej na ` +
                      `${STAV_LABELS[spat]}? Ešte nie je vyrobená?`,
                  )
                ) {
                  void nastav(spat, `Zákazka je späť: ${STAV_LABELS[spat]}`);
                }
              }}
            >
              Vrátiť na {STAV_LABELS[spat]}
            </DropdownMenuItem>
          </>
        ) : (
          <p className="border-t px-2 py-2 text-xs text-muted-foreground">
            Ponuka prejde do V príprave doplnením čísla AA alebo výrobnou
            variantou.
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
